import { useState } from "react";
import api from "../../../utils/api";
import {
  regroupThreadsByDate,
  setCachedHistory,
} from "../../../components/SideBar/cache/caching";

const authHeaders = (token) => ({
  headers: { Authorization: `Bearer ${token}` },
});

const DEEP_RESEARCH_IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp"];
export const isDeepResearchImageFile = (file) =>
  DEEP_RESEARCH_IMAGE_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext),
  );

export const askDeepResearch = (token, question, attachments = []) => {
  if (attachments.length === 0) {
    return api
      .post("/v3/deep-research", { question }, authHeaders(token))
      .then((response) => response.data);
  }

  const formData = new FormData();
  formData.append("question", question);
  attachments.forEach((file) => {
    formData.append(isDeepResearchImageFile(file) ? "images" : "files", file);
  });

  return api
    .post("/v3/deep-research", formData, authHeaders(token))
    .then((response) => response.data);
};

export const clarifyDeepResearch = (token, conversationId, clarification) =>
  api
    .post(
      `/v3/deep-research/${conversationId}/clarify`,
      { clarification },
      authHeaders(token),
    )
    .then((response) => response.data);

export const runDeepResearch = (token, conversationId) =>
  api
    .post(`/v3/deep-research/${conversationId}/run`, {}, authHeaders(token))
    .then((response) => response.data);

export const getDeepResearch = (token, conversationId) =>
  api
    .get(`/v3/deep-research/${conversationId}`, authHeaders(token))
    .then((response) => response.data);

export const getDeepResearchHistory = (token, limit = 20, offset = 0) =>
  api
    .get("/v3/deep-research", {
      ...authHeaders(token),
      params: { limit, offset },
    })
    .then((response) => response.data);

export const getDeepResearchErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return "Deep research request failed. Please try again.";
};

export const getDeepResearchErrorConversationId = (error) =>
  error?.response?.data?.detail?.conversation_id ?? null;

export const renameDeepResearch = (token, conversationId, title) =>
  api
    .patch(
      `/v3/deep-research/${conversationId}/title`,
      { title },
      authHeaders(token),
    )
    .then((response) => response.data);

export const deleteDeepResearch = (token, conversationId) =>
  api.delete(`/v3/deep-research/${conversationId}`, authHeaders(token));

const DEEP_RESEARCH_DRAFT_ID = "draft-deep-research";

const mapDeepResearchConversations = (items = []) =>
  items.map((item) => ({
    id: item.conversation_id,
    thread_id: item.conversation_id,
    name: item.title || item.question || "Deep Research",
    updated_at: item.completed_at || item.created_at,
    last_used: item.completed_at || item.created_at,
    thread: null,
    bot_role: "deep_research",
    bot_id: null,
  }));

export const getDeepResearchHistoryItems = (historyData = {}) =>
  Object.values(historyData || {})
    .flat()
    .filter((item) => item?.bot_role === "deep_research");

export const useDeepResearch = ({
  token,
  threadId,
  setThread,
  setThreadId,
  setBotRole,
  setMessages,
  setNewMessage,
  setSidebarVisible,
  setHistory,
}) => {
  const [deepResearchLoading, setDeepResearchLoading] = useState(false);
  const [deepResearchConversationId, setDeepResearchConversationId] =
    useState(null);

  const setLastMessage = (updater) => {
    setMessages((prevMessages) => {
      if (prevMessages.length === 0) return prevMessages;
      const updatedMessages = [...prevMessages];
      const lastIndex = updatedMessages.length - 1;
      updatedMessages[lastIndex] = updater(updatedMessages[lastIndex]);
      return updatedMessages;
    });
  };

  const fetchDeepResearchConversations = async () => {
    try {
      const response = await getDeepResearchHistory(token, 50, 0);
      return mapDeepResearchConversations(response?.items || []);
    } catch (error) {
      console.error("Error getting deep research history:", error);
      return [];
    }
  };

  const upsertDeepResearchHistoryItem = (conversationId, name, removeId) => {
    setHistory((prevHistory) => {
      const allItems = Object.values(prevHistory || {})
        .flat()
        .filter(Boolean);
      const existing = allItems.find(
        (item) => item.thread_id === conversationId,
      );
      const filtered = allItems.filter(
        (item) =>
          item.thread_id !== conversationId && item.thread_id !== removeId,
      );
      const timestamp = new Date().toISOString();
      const newItem = {
        id: conversationId,
        thread_id: conversationId,
        name: existing?.name || name,
        updated_at: timestamp,
        last_used: timestamp,
        thread: null,
        bot_role: "deep_research",
        bot_id: null,
      };
      const regrouped = regroupThreadsByDate([newItem, ...filtered]);
      setCachedHistory(regrouped);
      return regrouped;
    });
  };

  const removeDeepResearchDraft = () => {
    setHistory((prevHistory) => {
      const allItems = Object.values(prevHistory || {})
        .flat()
        .filter(Boolean);
      const filtered = allItems.filter(
        (item) => item.thread_id !== DEEP_RESEARCH_DRAFT_ID,
      );
      const regrouped = regroupThreadsByDate(filtered);
      setCachedHistory(regrouped);
      return regrouped;
    });
  };

  const startNewDeepResearchThread = () => {
    if (deepResearchLoading) return;
    setThread(null);
    setThreadId(DEEP_RESEARCH_DRAFT_ID);
    setBotRole("deep_research");
    setMessages([]);
    setDeepResearchConversationId(null);
    setNewMessage?.("");
    if (window.innerWidth < 640) setSidebarVisible?.(false);

    setHistory((prevHistory) => {
      const allItems = Object.values(prevHistory || {})
        .flat()
        .filter(Boolean);
      const filtered = allItems.filter(
        (item) => item.thread_id !== DEEP_RESEARCH_DRAFT_ID,
      );
      const timestamp = new Date().toISOString();
      const draftItem = {
        id: DEEP_RESEARCH_DRAFT_ID,
        thread_id: DEEP_RESEARCH_DRAFT_ID,
        name: "BETH AI Deep Research",
        updated_at: timestamp,
        last_used: timestamp,
        thread: null,
        bot_role: "deep_research",
        bot_id: null,
      };

      // Intentionally not persisted via setCachedHistory: the draft is a
      // local placeholder only, and should disappear on a real reload
      // rather than linger forever as an empty sidebar entry.
      return regroupThreadsByDate([draftItem, ...filtered]);
    });
  };

  const openDeepResearchThread = async (conversationId, knownQuestion) => {
    if (deepResearchLoading) return;
    if (conversationId === DEEP_RESEARCH_DRAFT_ID) {
      startNewDeepResearchThread();
      return;
    }
    setThread(null);
    setThreadId(conversationId);
    setBotRole("deep_research");
    setDeepResearchConversationId(null);
    removeDeepResearchDraft();
    if (window.innerWidth < 640) setSidebarVisible?.(false);

    const cachedMessages = JSON.parse(
      localStorage.getItem(`messages_${conversationId}`) || "null",
    );
    if (cachedMessages) {
      setMessages(cachedMessages);
      const lastDeepResearch =
        cachedMessages[cachedMessages.length - 1]?.deepResearch;
      if (lastDeepResearch?.status === "awaiting_clarification") {
        setDeepResearchConversationId(conversationId);
      }
      return;
    }

    setMessages([
      {
        role: "user",
        text_value: knownQuestion || "",
        timestamp: new Date().toISOString(),
      },
      {
        role: "assistant",
        text_value: "",
        deepResearch: { status: "loading" },
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const data = await getDeepResearch(token, conversationId);
      if (data.status === "awaiting_clarification") {
        setDeepResearchConversationId(conversationId);
      }
      const reconstructed = [
        {
          role: "user",
          text_value: knownQuestion || data.question || data.prompt || "",
          timestamp: new Date().toISOString(),
        },
        {
          role: "assistant",
          text_value: "",
          deepResearch: data,
          timestamp: new Date().toISOString(),
        },
      ];
      setMessages(reconstructed);
      localStorage.setItem(
        `messages_${conversationId}`,
        JSON.stringify(reconstructed),
      );
    } catch (error) {
      console.error("Error loading deep research thread:", error);
      setMessages([
        {
          role: "assistant",
          text_value: getDeepResearchErrorMessage(error),
          isStreamError: true,
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  };

  const handleDeepResearchSubmit = async (text, attachments = []) => {
    const trimmed = text.trim();
    if (!trimmed || deepResearchLoading) return;

    const isFollowUp = deepResearchConversationId !== null;
    const conversationIdBeforeCall = deepResearchConversationId;
    const threadIdBeforeCall = threadId;
    const filesToSend = isFollowUp ? [] : attachments;
    const imageAttachments = filesToSend.filter(isDeepResearchImageFile);
    const fileAttachments = filesToSend.filter(
      (file) => !isDeepResearchImageFile(file),
    );

    setNewMessage?.("");
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        role: "user",
        text_value: trimmed,
        file: fileAttachments.map((f) => f.name),
        image: imageAttachments.map((f) => ({
          name: f.name,
          url: URL.createObjectURL(f),
        })),
        timestamp: new Date().toISOString(),
      },
      {
        role: "assistant",
        text_value: "",
        deepResearch: { status: "loading" },
        timestamp: new Date().toISOString(),
      },
    ]);
    setDeepResearchLoading(true);

    try {
      const data = isFollowUp
        ? await clarifyDeepResearch(token, conversationIdBeforeCall, trimmed)
        : await askDeepResearch(token, trimmed, filesToSend);

      setDeepResearchConversationId(
        data.status === "awaiting_clarification" ? data.conversation_id : null,
      );
      setThreadId(data.conversation_id);
      upsertDeepResearchHistoryItem(
        data.conversation_id,
        isFollowUp ? null : trimmed,
        threadIdBeforeCall,
      );
      setLastMessage((message) => ({ ...message, deepResearch: data }));
    } catch (error) {
      const errorConversationId =
        getDeepResearchErrorConversationId(error) ?? conversationIdBeforeCall;
      setDeepResearchConversationId(errorConversationId);
      if (errorConversationId) {
        setThreadId(errorConversationId);
        upsertDeepResearchHistoryItem(
          errorConversationId,
          isFollowUp ? null : trimmed,
          threadIdBeforeCall,
        );
      }
      setLastMessage((message) => ({
        ...message,
        deepResearch: {
          status: "failed",
          message: getDeepResearchErrorMessage(error),
          conversation_id: errorConversationId,
        },
      }));
    } finally {
      setDeepResearchLoading(false);
    }
  };

  const handleDeepResearchRetry = async (conversationId) => {
    if (!conversationId || deepResearchLoading) return;

    setLastMessage((message) => ({
      ...message,
      deepResearch: { status: "loading" },
    }));
    setDeepResearchLoading(true);

    try {
      const data = await runDeepResearch(token, conversationId);
      setDeepResearchConversationId(
        data.status === "awaiting_clarification" ? data.conversation_id : null,
      );
      upsertDeepResearchHistoryItem(conversationId, null);
      setLastMessage((message) => ({ ...message, deepResearch: data }));
    } catch (error) {
      const errorConversationId =
        getDeepResearchErrorConversationId(error) ?? conversationId;
      setDeepResearchConversationId(errorConversationId);
      setLastMessage((message) => ({
        ...message,
        deepResearch: {
          status: "failed",
          message: getDeepResearchErrorMessage(error),
          conversation_id: errorConversationId,
        },
      }));
    } finally {
      setDeepResearchLoading(false);
    }
  };

  return {
    deepResearchLoading,
    deepResearchConversationId,
    setDeepResearchConversationId,
    startNewDeepResearchThread,
    openDeepResearchThread,
    handleDeepResearchSubmit,
    handleDeepResearchRetry,
    removeDeepResearchDraft,
    fetchDeepResearchConversations,
  };
};
